<script lang="ts" setup>
import { computed, inject, ref, watch } from "vue";

import actions from "~/actions";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import TaskMergeDropdown from "~/components/dumb/TaskMergeDropdown.vue";
import TaskOrDocLinkDropdown from "~/components/dumb/TaskOrDocLinkDropdown.vue";
import { DROPDOWN_REGISTER_SUBDROPDOWN_STATE } from "~/constants/injection";
import {
  OTHER_RELATIONSHIP_DROPDOWN_OPTIONS,
  type RelationshipDropdownItem,
  SUBTASK_RELATIONSHIP_DROPDOWN_OPTIONS,
} from "~/constants/relationship";
import { AttachmentFieldIcon, DocIcon, LinkFieldIcon, RelationshipsIcon } from "~/icons";
import { CommandId, DartboardKind, DropdownMenuItemKind, Placement, RelationshipDropdownKind } from "~/shared/enums";
import type { Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { openFilePicker } from "~/utils/common";
import { when } from "~/utils/wait";

const props = defineProps<{
  targetTask: Task;
  selectedTasks: Task[];
  itemIndex?: number;
}>();

const dropdownMenuRef = ref<InstanceType<typeof DropdownMenu> | null>(null);

const isOpen = computed(() => dropdownMenuRef.value?.isOpen ?? false);

const registerSubdropdownState = inject(DROPDOWN_REGISTER_SUBDROPDOWN_STATE, null);

watch(isOpen, (newValue) => {
  if (registerSubdropdownState && props.itemIndex !== undefined) {
    registerSubdropdownState(props.itemIndex, newValue);
  }
});

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const isMultipleTasks = computed(() => props.selectedTasks.length > 1);

const handleOptionClick = async (relationshipDropdownKind: RelationshipDropdownKind) => {
  switch (relationshipDropdownKind) {
    case RelationshipDropdownKind.CREATE_SUBTASK: {
      if (!appStore.selectedTaskDuids.has(props.targetTask.duid)) {
        actions.visualization.navigateToTask(props.targetTask.duid);
        await when(computed(() => appStore.selectedTaskDuids.has(props.targetTask.duid)));
        actions.visualization.createSubtask(props.targetTask);
        return;
      }
      // Wait for the context menu to close
      // oxlint-disable-next-line dart/no-set-timeout
      setTimeout(() => {
        actions.visualization.createSubtask();
      }, 100);
      break;
    }
    default: {
      throw new Error(`Unknown relationship dropdown kind: ${relationshipDropdownKind}`);
    }
  }
};

const buildTaskItem = (key: RelationshipDropdownKind, value: RelationshipDropdownItem) => {
  if (key === RelationshipDropdownKind.MERGED_INTO) {
    return {
      title: value.title,
      kind: DropdownMenuItemKind.COMPONENT,
      component: TaskMergeDropdown,
      componentArgs: {
        targetTask: props.targetTask,
        selectedTasks: props.selectedTasks,
      },
    };
  }
  if (key === RelationshipDropdownKind.CREATE_SUBTASK) {
    return {
      ...value,
      disabled: isMultipleTasks.value,
      onClick: () => handleOptionClick(key),
    };
  }
  return {
    title: value.title,
    kind: DropdownMenuItemKind.COMPONENT,
    component: TaskOrDocLinkDropdown,
    disabled: isMultipleTasks.value && key === RelationshipDropdownKind.SET_SUBTASK,
    componentArgs: {
      task: props.targetTask,
      tasks: props.selectedTasks,
      relationshipKindKind: value.relationshipKindKind,
      icon: value.icon,
      title: value.title,
      isForward: value.isForward,
      singleselect: key === RelationshipDropdownKind.SET_PARENT,
    },
  };
};

const sections = computed(() => {
  const isFinishedDartboard = appStore.currentPage?.kind === DartboardKind.FINISHED;

  const subtaskItems = [...SUBTASK_RELATIONSHIP_DROPDOWN_OPTIONS.entries()]
    .filter(([key]) => !isFinishedDartboard || key !== RelationshipDropdownKind.CREATE_SUBTASK)
    .map(([key, value]) => buildTaskItem(key, value));

  const otherTaskItems = [...OTHER_RELATIONSHIP_DROPDOWN_OPTIONS.entries()].map(([key, value]) =>
    buildTaskItem(key, value)
  );

  const docAndMoreItems = [
    {
      title: "Connect a doc",
      kind: DropdownMenuItemKind.COMPONENT,
      component: TaskOrDocLinkDropdown,
      componentArgs: {
        task: props.targetTask,
        isDoc: true,
        icon: DocIcon,
        title: "Link a doc",
      },
    },
    {
      title: "Add attachment",
      disabled: isMultipleTasks.value,
      kind: DropdownMenuItemKind.BUTTON,
      icon: AttachmentFieldIcon,
      commandId: CommandId.ADD_ATTACHMENT,
      onClick: () => {
        openFilePicker((files: File[]) => dataStore.addAttachments(props.targetTask.duid, files));
      },
    },
    {
      title: "Add link",
      disabled: isMultipleTasks.value,
      kind: DropdownMenuItemKind.BUTTON,
      icon: LinkFieldIcon,
      commandId: CommandId.ADD_LINK,
      onClick: () => {
        appStore.setLinkOpenInModal({ taskDuid: props.targetTask.duid });
      },
    },
  ];

  return [
    { title: "Subtasks", items: subtaskItems },
    { title: "Task", items: otherTaskItems },
    { title: "Doc and more", items: docAndMoreItems },
  ];
});
</script>

<template>
  <DropdownMenu
    ref="dropdownMenuRef"
    :sections="sections"
    :placement="Placement.RIGHT_TOP"
    mobile-include-back
    :prevent-close-on-select="pageStore.isMobile">
    <DropdownMenuItemContent :icon="RelationshipsIcon" title="Connect a task, doc, and more" is-submenu />
  </DropdownMenu>
</template>
